import React, { useState, useEffect } from "react";
import { siteInfo, amenities } from "../data/mockData";
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  TreePine,
  Target,
  Waves,
  ShieldCheck,
  ExternalLink,
} from "lucide-react";

const tabs = [
  { id: "teetimes", label: "Tee Times" },
  { id: "courseinfo", label: "Course Info" },
];

const TEEON_URL =
  "https://www.tee-on.com/PubGolf/servlet/com.teeon.teesheet.servlets.all.BookATeeTimeLanding?CourseCode=BLEN&LoginType=1&RestrictToType=false";

const Booking = () => {
  const [activeTab, setActiveTab] = useState("teetimes");
  const [iframeLoaded, setIframeLoaded] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#f8f7f4]">
      {/* Branded Header */}
      <section className="bg-[#1B2A4A] pt-28 pb-10">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-white/10 border border-[#C9A96E]/40 flex items-center justify-center text-[#C9A96E] font-serif text-xl font-bold shrink-0">
                WR
              </div>
              <div>
                <h1 className="text-white font-serif text-2xl font-bold leading-tight">
                  Willow Ridge Golf & Country Club
                </h1>
                <p className="text-[#C9A96E] text-sm font-medium tracking-wide">
                  Online Booking
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-emerald-500/15 border border-emerald-400/30 rounded-full">
              <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-emerald-300 text-sm font-medium">
                Online booking open
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200 sticky top-[72px] z-30">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <div className="flex gap-0">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`relative px-6 py-4 text-sm font-medium transition-colors duration-300 ${
                  activeTab === tab.id
                    ? "text-[#1B2A4A]"
                    : "text-gray-400 hover:text-gray-600"
                }`}
              >
                {tab.label}
                {activeTab === tab.id && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#C9A96E]" />
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab Panels */}
      <div className="max-w-5xl mx-auto px-6 lg:px-12 py-8">
        {/* Tee Times Panel */}
        {activeTab === "teetimes" && (
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <div className="relative" style={{ minHeight: "560px" }}>
              {!iframeLoaded && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-white z-10">
                  <div className="w-8 h-8 border-2 border-[#C9A96E] border-t-transparent rounded-full animate-spin" />
                  <span className="text-gray-400 text-sm">
                    Loading availability...
                  </span>
                </div>
              )}
              <iframe
                src={TEEON_URL}
                title="Book a Tee Time - TeeOn"
                scrolling="auto"
                frameBorder="0"
                onLoad={() => setIframeLoaded(true)}
                className="w-full border-none"
                style={{ height: "560px", display: "block" }}
                allow="cookies"
              />
            </div>
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-2 text-gray-400 text-xs">
                <ShieldCheck size={14} />
                Secure booking powered by Tee-On
              </div>
              <a
                href="https://www.tee-on.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-[#C9A96E] text-xs transition-colors flex items-center gap-1"
              >
                Tee-On Golf Systems
                <ExternalLink size={10} />
              </a>
            </div>
          </div>
        )}

        {/* Course Info Panel */}
        {activeTab === "courseinfo" && (
          <div className="space-y-8">
            {/* Course Overview Card */}
            <div className="bg-white rounded-2xl shadow-sm p-8">
              <h2 className="text-2xl font-serif font-bold text-[#1B2A4A] mb-6">
                Course Overview
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <p className="text-gray-600 leading-relaxed mb-4">
                    Willow Ridge Golf & Country Club features a beautifully
                    maintained 18-hole course that challenges golfers of all
                    skill levels. With lush fairways, strategic bunkers, and
                    pristine greens set among mature trees, every round is a
                    memorable experience.
                  </p>
                  <p className="text-gray-600 leading-relaxed">
                    Our course offers stunning countryside views and a variety of
                    hole designs that will test your accuracy and strategy.
                    Whether you're a seasoned player or just starting out, Willow
                    Ridge provides an exceptional golfing experience in the heart
                    of Chatham-Kent.
                  </p>
                </div>
                <div>
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="text-center p-4 bg-[#f8f7f4] rounded-xl">
                      <div className="w-10 h-10 mx-auto rounded-lg bg-[#C9A96E]/10 flex items-center justify-center mb-2">
                        <TreePine size={20} className="text-[#C9A96E]" />
                      </div>
                      <div className="text-xl font-bold text-[#1B2A4A]">18</div>
                      <div className="text-xs text-gray-500">Holes</div>
                    </div>
                    <div className="text-center p-4 bg-[#f8f7f4] rounded-xl">
                      <div className="w-10 h-10 mx-auto rounded-lg bg-[#C9A96E]/10 flex items-center justify-center mb-2">
                        <Target size={20} className="text-[#C9A96E]" />
                      </div>
                      <div className="text-xl font-bold text-[#1B2A4A]">72</div>
                      <div className="text-xs text-gray-500">Par</div>
                    </div>
                    <div className="text-center p-4 bg-[#f8f7f4] rounded-xl">
                      <div className="w-10 h-10 mx-auto rounded-lg bg-[#C9A96E]/10 flex items-center justify-center mb-2">
                        <Waves size={20} className="text-[#C9A96E]" />
                      </div>
                      <div className="text-xl font-bold text-[#1B2A4A]">6,400</div>
                      <div className="text-xs text-gray-500">Yards</div>
                    </div>
                  </div>
                  <div className="overflow-hidden rounded-xl">
                    <img
                      src="/images/golf_hero.jpg"
                      alt="Willow Ridge Golf Course"
                      className="w-full h-48 object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Amenities Card */}
            <div className="bg-white rounded-2xl shadow-sm p-8">
              <h2 className="text-2xl font-serif font-bold text-[#1B2A4A] mb-6">
                Club Amenities
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                {amenities.map((amenity, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 p-3 bg-[#f8f7f4] rounded-lg"
                  >
                    <div className="w-1.5 h-1.5 rounded-full bg-[#C9A96E] shrink-0" />
                    <span className="text-gray-600 text-sm">{amenity}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default Booking;
